import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Grid,
  GridItem,
  Button,
  DateRangePicker,
  Modal
} from "@sainsburys-tech/fable";
import FormWrapper from "../FormWrapper";
import apiProxy from "../../api/apiProxy";
import ButtonWithApiCall from "../ButtonWithApiCall";
import Papa from "papaparse";
import { Formik } from "formik";
import * as Yup from "yup";
import { AlertCircleFilled } from '@sainsburys-tech/icons'

moment.locale("en-GB");

const validationSchema = Yup.object().shape({
  exportFromDate: Yup.date()
    .typeError("Please enter a valid start date")
    .required("Please enter a date"),
  exportToDate: Yup.date()
    .typeError("Please enter a valid end date")
    .required("Please enter a date")
});

class ExportCsvButton extends React.Component {
  state = {
    data: []
  };

  downloadCsv = (data, filename) => {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  fetchData = () => {
    return apiProxy.sensor
      .getDataFromDingo(
        this.props.sensor.dingoId,
        "ThirtyMinutes",
        moment(this.props.exportFromDate)
          .utc()
          .set({
            hour: 0,
            minute: 0
          }),
        moment(this.props.exportToDate)
          .utc()
          .set({ hour: 23, minute: 59 }),
        true
      )
      .then(sensorData => {
        if (sensorData) {
          const csvDataHeader = [
            ["Sensor Name:", this.props.sensor.name],
            ["Sensor ID:", this.props.sensor.sensorId],
            ["Store ID:", this.props.storeId],
            ["Exported At (UTC):", new Date().toISOString()],
            ["Time (UTC)", "Sensor Reading"]
          ];
          const csvData = csvDataHeader.concat(sensorData.data.values);
          const filename = this.props.sensor.name.concat(
            "-",
            moment(this.props.exportFromDate).toISOString(),
            ".csv"
          );
          this.downloadCsv(csvData, filename);
          this.props.onExportSuccess();
        } else {
          this.props.handleAPIError();
        }
      });
  };

  render() {
    return (
      <div>
        <ButtonWithApiCall
          fullWidth
          label="Export CSV"
          type="submit"
          savingMessage="Exporting data"
          useInlineError
          onClick={this.fetchData}
          disabled={!this.props.isValid}
        />
      </div>
    );
  }
}

const ExportSensorDataModal = props => {
  return (
    <Modal
      isOpen={props.open}
      onClose={props.onClose}
    >
      <h3 className="fable:mb-4">
        <br />
        Export '{props.sensor.name}' sensor data
      </h3>
      <p>Export energy data from the selected date ranges.</p>
      <br />
      <Formik
        enableReinitialize
        initialValues={props.initialValues}
        isInitialValid={() => {
          return validationSchema.isValidSync(props.initialValues);
        }}
        touched={{}}
        validateOnChange={true}
        validationSchema={validationSchema}
        render={formProps => (
          <div>
            <FormWrapper isDirty={formProps.dirty}>
              <fieldset>
                <legend style={{ fontSize: '1rem', fontWeight: '600' }}>Select a date</legend>
                <div className="cb-c-export-sensor-form__date-input">
                  <DateRangePicker
                    label="Date Range"
                    hasHiddenLabel={true}
                    start={{
                      value: formProps.values.exportFromDate
                        ? moment(formProps.values.exportFromDate).format("YYYY-MM-DD")
                        : "",
                      label: "From",
                      placeholder: "DD/MM/YY"
                    }}
                    end={{
                      value: formProps.values.exportToDate
                        ? moment(formProps.values.exportToDate).format("YYYY-MM-DD")
                        : "",
                      label: "To",
                      placeholder: "DD/MM/YY"
                    }}
                    range={{
                      from: moment().format("YYYY-MM-DD"),
                      state: "disabled"
                    }}
                    shouldCloseOnSelect={true}
                    weekStartDay="monday"
                    onRangeChange={(startDate, endDate) => {
                      if (startDate && moment(startDate, "YYYY-MM-DD").isValid()) {
                        formProps.setFieldValue(
                          "exportFromDate",
                          moment(startDate, "YYYY-MM-DD")
                        );
                      }
                      if (endDate && moment(endDate, "YYYY-MM-DD").isValid()) {
                        formProps.setFieldValue(
                          "exportToDate",
                          moment(endDate, "YYYY-MM-DD")
                        );
                      }
                      if (
                        startDate &&
                        moment(startDate, "YYYY-MM-DD").isValid() &&
                        endDate &&
                        moment(endDate, "YYYY-MM-DD").isValid()
                      ) {
                        props.setDateValidation(true);
                      } else {
                        props.setDateValidation(false);
                      }
                    }}
                    status={
                      (formProps.touched.exportFromDate &&
                        formProps.errors.exportFromDate) ||
                      (formProps.touched.exportToDate &&
                        formProps.errors.exportToDate)
                        ? "error"
                        : undefined
                    }
                  />
                </div>
              </fieldset>
              {props.hasAPIError === true && (
                <div className="fable:pb-8">
                  <AlertCircleFilled className="fable:mr-8 fable:text-red-600" />
                  <b>
                    There was a problem downloading the data, please try again
                    later
                  </b>
                </div>
              )}
              <Grid cols={{ xl: 3, lg: 3, md: 5, sm: 2, xs: 1 }} gap="md">
                <GridItem colSpan={{ xl: 1, lg: 1, md: 2, sm: 1, xs: 1 }}>
                  <Button variant="secondary" fullWidth onClick={props.onClose}>
                    Cancel
                  </Button>
                </GridItem>

                <GridItem colSpan={{ xl: 1, lg: 1, md: 2, sm: 1, xs: 1 }}>
                  <ExportCsvButton
                    isValid={props.isValidDate}
                    sensor={props.sensor}
                    storeId={props.storeId}
                    exportFromDate={formProps.values.exportFromDate}
                    exportToDate={formProps.values.exportToDate}
                    onExportSuccess={props.onExportSuccess}
                    handleAPIError={props.handleAPIError}
                  />
                </GridItem>
              </Grid>
            </FormWrapper>
          </div>
        )}
      />
    </Modal>
  );
};

ExportSensorDataModal.propTypes = {
  storeId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onExportSuccess: PropTypes.func.isRequired,
  sensor: PropTypes.object.isRequired
};

export default ExportSensorDataModal;
